import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "../lib/api";
import {
  resumeSession,
  startAudit,
  getIntakeOptions,
  submitSnapshot,
} from "../lib/api/index";
import { logger } from "../lib/logger";

// P2 — Business Snapshot. The pre-diagnostic intake step. Captures
// CEO-locked 6 REQUIRED fields + 10 HIGH-VALUE OPTIONAL fields, then
// drops the user straight into the 12-question diagnostic with q1
// pre-filled from the Snapshot Industry value.
//
// Endpoint contract:
//   POST /audit/start         → mints session_id (cookie) if not already set
//   POST /audit/snapshot      → persists fields, writes q1 answer, no scoring
//   → navigate('/audit')      → resumes by cookie, Q1 pre-selected
//
// All dropdown options come from GET /api/audit/intake-options.
// Industry options come from the canonical q1_business_type choices
// hard-coded here to match the backend's _SNAPSHOT_INDUSTRY_OPTIONS.

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_CHARS_RE = /^[\d+\-() ]+$/;

const INDUSTRY_OPTIONS = [
  "Service business",
  "Coaching or consulting",
  "Agency",
  "E-commerce",
  "SaaS or software",
  "Other",
];

const EMPTY_FORM = {
  owner_name: "",
  email: "",
  phone: "",
  business_name: "",
  website: "",
  industry: "",
  industry_other: "",
  // optional
  city: "",
  state: "",
  years_in_business: "",
  employee_count: "",
  annual_revenue_range: "",
  primary_service: "",
  primary_lead_source: "",
  biggest_challenge: "",
  growth_goal_12mo: "",
  tools_snapshot: "",
};

const LABEL_STYLE = {
  display: "block", fontSize: 12, color: "var(--sf-muted)",
  letterSpacing: "0.12em", textTransform: "uppercase",
  fontFamily: "var(--sf-mono, monospace)", marginBottom: 6,
};
const FIELD_GRID = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 };
const SECTION_HEADER = { fontSize: 18, marginBottom: 4 };
const SECTION_HINT = { fontSize: 13, color: "var(--sf-muted)", marginBottom: 16 };

function Field({ name, label, required, error, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label htmlFor={`snapshot-${name}`} style={LABEL_STYLE}>
        {label}{required ? " *" : ""}
      </label>
      {children}
      {error && (
        <div
          className="sf-mono"
          style={{ color: "#e2867e", fontSize: 12, marginTop: 4 }}
          data-testid={`snapshot-error-${name}`}
        >
          {error}
        </div>
      )}
    </div>
  );
}

export default function Snapshot() {
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState(null);
  const [options, setOptions] = useState(null);
  const [showOptional, setShowOptional] = useState(false);

  // Mount event
  useEffect(() => {
    trackEvent("intake_form_view", "/audit/snapshot", { platform: "web" });
  }, []);

  // Resume by cookie first; if none, start a fresh session.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await resumeSession();
        if (cancelled) return;
        if (data?.session_id) {
          setSessionId(data.session_id);
        } else {
          throw new Error("no_session");
        }
      } catch (err) {
        if (cancelled) return;
        // No active session — start fresh.
        try {
          const start = await startAudit({});
          if (cancelled) return;
          setSessionId(start.session_id);
        } catch (startErr) {
          logger.error("snapshot_start_failed", { err: startErr?.response?.data || startErr?.message });
          if (startErr?.response?.status === 429) {
            setGlobalError(
              startErr.response?.data?.detail?.message
                || "Too many audits started from this IP. Try again later."
            );
          } else {
            setGlobalError("Could not begin the audit. Please refresh and try again.");
          }
        }
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch option lists for the optional dropdowns
  useEffect(() => {
    let cancelled = false;
    getIntakeOptions()
      .then((data) => { if (!cancelled) setOptions(data?.options || {}); })
      .catch((err) => {
        if (cancelled) return;
        logger.error("snapshot_options_load_failed", { err: err?.message });
        setOptions({});
      });
    return () => { cancelled = true; };
  }, []);

  const setField = useCallback((name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  // Required-only gate. Server-side validation enforces format.
  // P3 / O1a — website is allowed to be empty, "none", "n/a", "no website",
  // or a real-looking URL. Backend re-validates and 422s on actual junk.
  const NO_WEBSITE_TOKENS = ["", "none", "n/a", "na", "no", "no website", "nowebsite", "-"];
  const websiteRaw = (form.website || "").trim();
  const websiteOk =
    NO_WEBSITE_TOKENS.includes(websiteRaw.toLowerCase())
    || (websiteRaw.includes(".") && !websiteRaw.includes(" "));
  const requiredFilled = Boolean(
    form.owner_name.trim()
    && EMAIL_RE.test(form.email.trim())
    && form.phone.trim().length >= 7
    && PHONE_CHARS_RE.test(form.phone.trim())
    && form.business_name.trim()
    && websiteOk
    && form.industry.trim()
    && (form.industry !== "Other" || form.industry_other.trim())
  );

  const onSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (submitting || !sessionId) return;
    setErrors({});
    setGlobalError(null);
    setSubmitting(true);

    const trimOrNull = (s) => {
      const t = (s || "").trim();
      return t ? t : null;
    };
    const profile = {
      years_in_business: trimOrNull(form.years_in_business),
      employee_count: trimOrNull(form.employee_count),
      annual_revenue_range: trimOrNull(form.annual_revenue_range),
      primary_service: trimOrNull(form.primary_service),
      primary_lead_source: trimOrNull(form.primary_lead_source),
    };
    const intent = {
      biggest_challenge: trimOrNull(form.biggest_challenge),
      growth_goal_12mo: trimOrNull(form.growth_goal_12mo),
    };
    const anyProfile = Object.values(profile).some((v) => v !== null);
    const anyIntent = Object.values(intent).some((v) => v !== null);

    const payload = {
      session_id: sessionId,
      owner_name: form.owner_name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      business_name: form.business_name.trim(),
      website: form.website.trim(),
      industry: form.industry.trim(),
      industry_other: form.industry === "Other" ? form.industry_other.trim() : null,
      city: trimOrNull(form.city),
      state: trimOrNull(form.state),
      tools_snapshot: trimOrNull(form.tools_snapshot),
      business_profile: anyProfile ? profile : null,
      growth_intent: anyIntent ? intent : null,
    };

    try {
      await submitSnapshot(payload);
      trackEvent(
        "intake_form_submit_success",
        "/audit/snapshot",
        {
          platform: "web",
          has_profile: anyProfile,
          has_intent: anyIntent,
          has_tools_snapshot: Boolean(payload.tools_snapshot),
          industry: payload.industry,
        },
        sessionId,
      );
      navigate("/audit");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 422 && detail && typeof detail === "object" && detail.errors) {
        setErrors(detail.errors);
        setGlobalError("Please correct the highlighted fields and try again.");
      } else {
        logger.error("snapshot_submit_failed", { err: err?.response?.data || err?.message });
        setGlobalError("Could not save your business snapshot. Try again in a moment.");
      }
      setSubmitting(false);
    }
  }, [submitting, sessionId, form, navigate]);

  if (bootstrapping) {
    return (
      <div className="sf-container" style={{ padding: "80px 24px" }} data-testid="snapshot-bootstrapping">
        <div className="sf-muted-text">Starting your audit…</div>
      </div>
    );
  }
  if (!sessionId) {
    return (
      <div className="sf-container" style={{ padding: "80px 24px" }} data-testid="snapshot-no-session">
        <div className="sf-mono" style={{ color: "#e2867e" }}>
          {globalError || "Could not start your audit. Please refresh."}
        </div>
      </div>
    );
  }

  const opt = (name) => (options?.[name] || []);

  return (
    <div className="sf-container" style={{ padding: "48px 24px", maxWidth: 760 }} data-testid="snapshot-page">
      <div className="sf-eyebrow" style={{ marginBottom: 10 }} data-testid="snapshot-eyebrow">
        Business Snapshot
      </div>
      <h1 className="sf-display" style={{ fontSize: 34, marginBottom: 10, lineHeight: 1.15 }}>
        Tell us about your business.
      </h1>
      <p className="sf-muted-text" style={{ marginBottom: 28 }}>
        Tell us a little about your business so the diagnostic compares you to
        the right benchmarks. About a minute. Then the 12-question audit.
      </p>

      <form className="sf-card" onSubmit={onSubmit} data-testid="snapshot-form" noValidate>
        {/* --- You ----------------------------------------------------- */}
        <div style={{ marginBottom: 6 }}>
          <h2 className="sf-display" style={SECTION_HEADER}>You</h2>
          <p style={SECTION_HINT}>So we can deliver your audit.</p>
        </div>
        <div style={FIELD_GRID}>
          <Field name="owner_name" label="Full name" required error={errors.owner_name}>
            <input
              id="snapshot-owner_name"
              data-testid="snapshot-owner_name-input"
              type="text"
              className="sf-input"
              value={form.owner_name}
              maxLength={120}
              onChange={(e) => setField("owner_name", e.target.value)}
            />
          </Field>
          <Field name="email" label="Email" required error={errors.email}>
            <input
              id="snapshot-email"
              data-testid="snapshot-email-input"
              type="email"
              className="sf-input"
              value={form.email}
              maxLength={200}
              onChange={(e) => setField("email", e.target.value)}
            />
          </Field>
        </div>
        <Field name="phone" label="Phone" required error={errors.phone}>
          <input
            id="snapshot-phone"
            data-testid="snapshot-phone-input"
            type="tel"
            className="sf-input"
            value={form.phone}
            maxLength={24}
            onChange={(e) => setField("phone", e.target.value)}
          />
        </Field>

        {/* --- Your Business ------------------------------------------ */}
        <div style={{ marginTop: 22, marginBottom: 6 }}>
          <h2 className="sf-display" style={SECTION_HEADER}>Your business</h2>
          <p style={SECTION_HINT}>Used to calibrate the diagnostic.</p>
        </div>
        <Field name="business_name" label="Business name" required error={errors.business_name}>
          <input
            id="snapshot-business_name"
            data-testid="snapshot-business_name-input"
            type="text"
            className="sf-input"
            value={form.business_name}
            maxLength={160}
            onChange={(e) => setField("business_name", e.target.value)}
          />
        </Field>
        <Field name="website" label="Website (if you have one)" required error={errors.website}>
          <input
            id="snapshot-website"
            data-testid="snapshot-website-input"
            type="url"
            className="sf-input"
            placeholder="https://"
            value={form.website}
            maxLength={200}
            onChange={(e) => setField("website", e.target.value)}
          />
        </Field>
        <Field name="industry" label="Industry" required error={errors.industry}>
          <select
            id="snapshot-industry"
            data-testid="snapshot-industry-select"
            className="sf-input"
            value={form.industry}
            onChange={(e) => setField("industry", e.target.value)}
          >
            <option value="">Select…</option>
            {INDUSTRY_OPTIONS.map((label) => (
              <option key={label} value={label}>{label}</option>
            ))}
          </select>
        </Field>
        {form.industry === "Other" && (
          <Field name="industry_other" label="What industry are you in?" required error={errors.industry_other}>
            <input
              id="snapshot-industry_other"
              data-testid="snapshot-industry_other-input"
              type="text"
              className="sf-input"
              value={form.industry_other}
              maxLength={80}
              onChange={(e) => setField("industry_other", e.target.value)}
            />
          </Field>
        )}

        {/* --- Optional context (collapsed by default) ----------------- */}
        <div style={{ marginTop: 22 }}>
          <button
            type="button"
            className="sf-btn-secondary"
            data-testid="snapshot-toggle-optional"
            onClick={() => setShowOptional((v) => !v)}
            style={{ width: "100%" }}
          >
            {showOptional
              ? "Hide optional context"
              : "Add optional context (more accurate benchmarks)"}
          </button>
        </div>

        {showOptional && (
          <div data-testid="snapshot-optional-section" style={{ marginTop: 18 }}>
            <p style={SECTION_HINT}>
              All optional. More context here = more specific findings in your audit.
            </p>
            <div style={FIELD_GRID}>
              <Field name="city" label="City" error={errors.city}>
                <input
                  id="snapshot-city" data-testid="snapshot-city-input" type="text"
                  className="sf-input" value={form.city} maxLength={80}
                  onChange={(e) => setField("city", e.target.value)}
                />
              </Field>
              <Field name="state" label="State / Region" error={errors.state}>
                <input
                  id="snapshot-state" data-testid="snapshot-state-input" type="text"
                  className="sf-input" value={form.state} maxLength={80}
                  onChange={(e) => setField("state", e.target.value)}
                />
              </Field>
            </div>
            <div style={FIELD_GRID}>
              <Field name="years_in_business" label="Years in business">
                <select
                  id="snapshot-years_in_business"
                  data-testid="snapshot-years_in_business-select"
                  className="sf-input"
                  value={form.years_in_business}
                  onChange={(e) => setField("years_in_business", e.target.value)}
                >
                  <option value="">Select…</option>
                  {opt("years_in_business").map((label) => (
                    <option key={label} value={label}>{label}</option>
                  ))}
                </select>
              </Field>
              <Field name="employee_count" label="Team size">
                <select
                  id="snapshot-employee_count"
                  data-testid="snapshot-employee_count-select"
                  className="sf-input"
                  value={form.employee_count}
                  onChange={(e) => setField("employee_count", e.target.value)}
                >
                  <option value="">Select…</option>
                  {opt("employee_count").map((label) => (
                    <option key={label} value={label}>{label}</option>
                  ))}
                </select>
              </Field>
            </div>
            <Field name="annual_revenue_range" label="Annual revenue range">
              <select
                id="snapshot-annual_revenue_range"
                data-testid="snapshot-annual_revenue_range-select"
                className="sf-input"
                value={form.annual_revenue_range}
                onChange={(e) => setField("annual_revenue_range", e.target.value)}
              >
                <option value="">Select…</option>
                {opt("annual_revenue_range").map((label) => (
                  <option key={label} value={label}>{label}</option>
                ))}
              </select>
            </Field>
            <Field name="primary_service" label="Primary service / product" error={errors.primary_service}>
              <input
                id="snapshot-primary_service" data-testid="snapshot-primary_service-input"
                type="text" className="sf-input"
                value={form.primary_service} maxLength={280}
                onChange={(e) => setField("primary_service", e.target.value)}
              />
            </Field>
            <Field name="primary_lead_source" label="Primary lead source">
              <select
                id="snapshot-primary_lead_source"
                data-testid="snapshot-primary_lead_source-select"
                className="sf-input"
                value={form.primary_lead_source}
                onChange={(e) => setField("primary_lead_source", e.target.value)}
              >
                <option value="">Select…</option>
                {opt("primary_lead_source").map((label) => (
                  <option key={label} value={label}>{label}</option>
                ))}
              </select>
            </Field>
            <Field name="tools_snapshot" label="Current CRM / tools" error={errors.tools_snapshot}>
              <input
                id="snapshot-tools_snapshot" data-testid="snapshot-tools_snapshot-input"
                type="text" className="sf-input"
                value={form.tools_snapshot} maxLength={280}
                placeholder="HubSpot, Calendly, Slack…"
                onChange={(e) => setField("tools_snapshot", e.target.value)}
              />
            </Field>
            <Field name="biggest_challenge" label="Biggest challenge right now" error={errors.biggest_challenge}>
              <textarea
                id="snapshot-biggest_challenge" data-testid="snapshot-biggest_challenge-input"
                className="sf-input"
                rows={3}
                value={form.biggest_challenge} maxLength={500}
                onChange={(e) => setField("biggest_challenge", e.target.value)}
              />
            </Field>
            <Field name="growth_goal_12mo" label="12-month growth goal">
              <select
                id="snapshot-growth_goal_12mo"
                data-testid="snapshot-growth_goal_12mo-select"
                className="sf-input"
                value={form.growth_goal_12mo}
                onChange={(e) => setField("growth_goal_12mo", e.target.value)}
              >
                <option value="">Select…</option>
                {opt("growth_goal_12mo").map((label) => (
                  <option key={label} value={label}>{label}</option>
                ))}
              </select>
            </Field>
          </div>
        )}

        {globalError && (
          <div
            className="sf-mono"
            style={{ color: "#e2867e", fontSize: 13, marginTop: 18 }}
            data-testid="snapshot-global-error"
          >
            {globalError}
          </div>
        )}

        <div style={{ marginTop: 24, display: "flex", gap: 12, alignItems: "center" }}>
          <button
            type="submit"
            className="sf-btn"
            data-testid="snapshot-submit"
            disabled={!requiredFilled || submitting}
          >
            {submitting ? "Saving…" : "Start the diagnostic →"}
          </button>
          <span className="sf-muted-text" style={{ fontSize: 12 }}>
            12 quick questions follow.
          </span>
        </div>
      </form>
    </div>
  );
}
